import React, { useMemo } from "react";
import PedigreeNode from "./PedigreeNode";
import { buildRepeatedConditionMap } from "@/lib/conditions";
import {
  emptyAncestor,
  SIRE,
  DAM,
  totalAncestors,
  generationOf,
  indexInGeneration,
} from "@/lib/quality";

// Per-generation node sizing (W x H) and symbol size.
const GEN_STYLE = [
  { w: 210, h: 150, symbol: 96 }, // gen 0 – subject
  { w: 190, h: 135, symbol: 84 }, // gen 1
  { w: 160, h: 118, symbol: 70 }, // gen 2
  { w: 138, h: 102, symbol: 58 }, // gen 3
  { w: 116, h: 90,  symbol: 48 }, // gen 4
  { w: 96,  h: 78,  symbol: 38 }, // gen 5
  { w: 80,  h: 62,  symbol: 32 }, // gen 6
  { w: 64,  h: 50,  symbol: 26 }, // gen 7
  { w: 52,  h: 42,  symbol: 22 }, // gen 8
  { w: 44,  h: 36,  symbol: 18 }, // gen 9
];

// Vertical spacing between LEAF generation slots (per generation count).
const LEAF_SLOT_HEIGHT = {
  4: 100,
  5: 86,
  6: 76,
  7: 60,
  8: 50,
  9: 44,
  10: 40,
};

// Horizontal gap before each generation column.
const COL_GAPS = [30, 30, 26, 22, 20, 18, 16, 14, 12, 10];

function genLabel(index) {
  if (index === 0) return "Subject";
  // Build label from path of S/D bits (skip top subject bit)
  let i = index;
  const path = [];
  while (i > 0) {
    path.unshift(i % 2 === 1 ? "Sire" : "Dam");
    i = Math.floor((i - 1) / 2);
  }
  return path.join(" › ");
}

// Print-friendly variants — natively sized to fit BOTH Chrome's and Safari's
// Letter-landscape printable area at 100% scale. Safari's default print
// margins are ~0.5in/side so usable area is ≈ 960×720. We target ≈ 920×560
// so the surrounding header/subject/footer (~140px) leaves room for the tree.
const PRINT_GEN_STYLE = [
  { w: 180, h: 70, symbol: 44 }, // gen 0  (Subject)
  { w: 168, h: 60, symbol: 38 }, // gen 1
  { w: 156, h: 48, symbol: 30 }, // gen 2
  { w: 142, h: 38, symbol: 24 }, // gen 3
  { w: 130, h: 30, symbol: 18 }, // gen 4  (16 leaf cells)
];
const PRINT_LEAF_SLOT_HEIGHT = { 4: 70, 5: 32, 6: 22 };
const PRINT_COL_GAPS = [6, 10, 12, 12, 10];

// 3-generation print variant — fewer nodes (7 total), so we stretch each
// card to make the ancestor info comfortably readable on a Safari-printed
// page. Tuned to fit landscape Letter usable area (~960×680) with safety
// margin so Safari can never push the footer onto a second page.
//   gen 0:  280×150 card (one centred subject)
//   gen 1:  260×130 (two stacked)
//   gen 2:  220×115 (four leaves)
const PRINT_GEN_STYLE_3 = [
  { w: 280, h: 150, symbol: 80 },
  { w: 260, h: 130, symbol: 64 },
  { w: 220, h: 115, symbol: 48 },
];
const PRINT_LEAF_SLOT_HEIGHT_3 = { 3: 125 };
const PRINT_COL_GAPS_3 = [20, 40, 60];

export const PedigreeTree = ({
  generations,
  ancestors,
  selectedIndex,
  onSelect,
  onSwap,
  draggable = false,
  compact = false,
  printStretched = false,
}) => {
  const repeatedMap = useMemo(() => buildRepeatedConditionMap(ancestors || {}), [ancestors]);
  const styleSet = printStretched
    ? PRINT_GEN_STYLE_3
    : (compact ? PRINT_GEN_STYLE : GEN_STYLE);
  const slotMap = printStretched
    ? PRINT_LEAF_SLOT_HEIGHT_3
    : (compact ? PRINT_LEAF_SLOT_HEIGHT : LEAF_SLOT_HEIGHT);
  const colGaps = printStretched
    ? PRINT_COL_GAPS_3
    : (compact ? PRINT_COL_GAPS : COL_GAPS);

  const layout = useMemo(() => {
    const total = totalAncestors(generations);
    const leafGen = generations - 1;
    const leafCount = Math.pow(2, leafGen);
    const slotH = slotMap[generations] || 80;
    const stageHeight = leafCount * slotH + 40;

    // Compute x positions per generation column
    const xPositions = [];
    let x = 0;
    for (let g = 0; g <= leafGen; g++) {
      x += colGaps[g] || 18;
      xPositions.push(x);
      const s = styleSet[g] || styleSet[styleSet.length - 1];
      x += s.w;
    }
    const stageWidth = x + 24;

    // Compute (cx, cy) for each node
    const positions = [];
    for (let i = 0; i < total; i++) {
      const g = generationOf(i);
      const p = indexInGeneration(i);
      const inGen = Math.pow(2, g);
      // Center of slot for this node = leafCount/inGen leaf slots wide
      const slotsPerNode = leafCount / inGen;
      const cy = (p * slotsPerNode + slotsPerNode / 2) * slotH + 20;
      const s = styleSet[g] || styleSet[styleSet.length - 1];
      positions.push({
        i,
        g,
        cx: xPositions[g] + s.w / 2,
        cy,
        w: s.w,
        h: s.h,
        symbol: s.symbol,
        x: xPositions[g],
        y: cy - s.h / 2,
      });
    }
    return { positions, stageWidth, stageHeight, total, leafGen };
  }, [generations, styleSet, slotMap, colGaps]);

  // Build connector path data: each non-leaf node connects right-side to its two children's left-side.
  const connectors = useMemo(() => {
    const paths = [];
    for (let i = 0; i < layout.total; i++) {
      const sire = SIRE(i);
      const dam = DAM(i);
      if (sire >= layout.total) continue;
      const parent = layout.positions[i];
      const ps = layout.positions[sire];
      const pd = layout.positions[dam];
      // Start at right edge midpoint of parent
      const x0 = parent.x + parent.w;
      const y0 = parent.cy;
      // End at left edge midpoint of each child
      const x1s = ps.x;
      const y1s = ps.cy;
      const x1d = pd.x;
      const y1d = pd.cy;
      const midX = (x0 + x1s) / 2;
      paths.push(
        `M ${x0} ${y0} H ${midX} V ${y1s} H ${x1s}`,
        `M ${x0} ${y0} H ${midX} V ${y1d} H ${x1d}`,
      );
    }
    return paths;
  }, [layout]);

  return (
    <div
      className="relative pedigree-stage"
      data-testid="pedigree-stage"
      style={{ width: layout.stageWidth, height: layout.stageHeight }}
    >
      {/* Connector lines layer */}
      <svg
        className="connector absolute inset-0 pointer-events-none"
        width={layout.stageWidth}
        height={layout.stageHeight}
      >
        {connectors.map((d, idx) => (
          <path key={idx} d={d} />
        ))}
      </svg>

      {/* Generation header strip */}
      <div
        className="absolute top-0 left-0 right-0 flex pointer-events-none"
        style={{ height: 18 }}
      >
        {Array.from({ length: generations }).map((_, g) => {
          const s = GEN_STYLE[g] || GEN_STYLE[GEN_STYLE.length - 1];
          // Find first position in this generation to grab x
          const first = layout.positions.find((p) => p.g === g);
          if (!first) return null;
          return (
            <div
              key={g}
              className="absolute text-[10px] uppercase tracking-[0.2em] font-display font-semibold text-[var(--app-text-soft)]"
              style={{ left: first.x, top: -4, width: s.w, textAlign: "center" }}
            >
              {g === 0 ? "Subject" : `Gen ${g}`}
            </div>
          );
        })}
      </div>

      {/* Nodes */}
      {layout.positions.map((p) => (
        <div
          key={p.i}
          className="absolute"
          style={{ left: p.x, top: p.y, width: p.w, height: p.h }}
        >
          <PedigreeNode
            index={p.i}
            ancestor={ancestors[p.i] || emptyAncestor()}
            onClick={onSelect}
            selected={p.i === selectedIndex}
            width={p.w}
            height={p.h}
            symbolSize={p.symbol}
            label={genLabel(p.i)}
            showText={p.g <= 4}
            generation={p.g}
            repeatedMap={repeatedMap}
          />
        </div>
      ))}
    </div>
  );
};

export default PedigreeTree;
